/**
 * FocusSetsPanel.jsx — Settings ▸ Team ▸ Focus sets (admin only).
 *
 * Chooses, per person, what their Focus switch hides. Each item shows that
 * person's own use over the telemetry window, so the call is made on data:
 * screens by page views, everything else by clicks on its data-focus key.
 */
import { useMemo, useState } from 'react';
import { useProfiles } from '../../hooks/useProfiles';
import { useFocus, useSetFocusHidden, useFocusUsage } from '../../hooks/useFocus';
import { NAV_ITEMS } from '../layout/Sidebar.mui';
import { FOCUS_ELEMENTS, navKey, navSubKey, recommendedFor } from '../../lib/focusRegistry';
import { useToast } from '../layout/Toast';
import { cn } from '../../lib/utils';

// Screens that only the mobile More sheet lists.
const MOBILE_ONLY = [
  { path: '/calculator', label: 'Calculator (mobile)' },
  { path: '/projects', label: 'Projects (mobile)' },
];

// Each block is a screen with its flyout items, kept together across columns.
function buildGroups() {
  const nav = [];
  for (const item of [...NAV_ITEMS, ...MOBILE_ONLY.filter((m) => !NAV_ITEMS.some((n) => n.path === m.path))]) {
    nav.push([
      { key: navKey(item.path), label: item.label, path: item.path },
      ...(item.flyout || []).map((t) => ({ key: navSubKey(item.path, t), label: t.label, sub: true })),
    ]);
  }
  const groups = [{ screen: 'Navigation', blocks: nav }];
  for (const el of FOCUS_ELEMENTS) {
    let g = groups.find((x) => x.screen === el.screen);
    if (!g) groups.push((g = { screen: el.screen, blocks: [[]] }));
    g.blocks[0].push(el);
  }
  return groups;
}

// Clicks have only been recorded since click tracking shipped; until there's a
// couple of weeks of history a zero means "no data yet", not "unused".
const CLICK_HISTORY_DAYS = 14;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

function MemberEditor({ profile, current, onClose }) {
  const groups = useMemo(buildGroups, []);
  const { data: usage, isLoading, isError, refetch } = useFocusUsage(profile.id);
  // Missing usage must never read as "0 views" — that would mark every screen unused.
  const loaded = !!usage;
  const save = useSetFocusHidden();
  const { toast } = useToast();
  const [hidden, setHidden] = useState(() => new Set(current));

  const useOf = (item) => {
    const page = item.path ? usage?.pages?.[item.path] : null;
    const clicks = usage?.clicks?.[item.key];
    return { views: page?.n || 0, clicks: clicks?.n || 0, last: page?.last || clicks?.last };
  };

  const toggle = (key) => setHidden((h) => {
    const n = new Set(h);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  // Screens this person never opened in the window. Sub-items and in-screen
  // controls are left alone until click telemetry has enough history to judge them.
  const clickDays = usage?.clicksSince ? (Date.now() - new Date(usage.clicksSince)) / 86_400_000 : 0;
  const clicksMeaningful = clickDays >= CLICK_HISTORY_DAYS;

  const suggest = () => {
    const unused = groups[0].blocks.map((b) => b[0]).filter((i) => i.path !== '/' && useOf(i).views === 0).map((i) => i.key);
    setHidden((h) => new Set([...h, ...unused]));
  };

  const handleSave = async () => {
    try {
      await save.mutateAsync({ profileId: profile.id, hidden: [...hidden] });
      toast(`Focus set saved for ${profile.fullName || profile.email}`);
      onClose();
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-dim">
        {isError && !loaded ? (
          <span className="text-red-accent">
            Couldn't load usage.{' '}
            <button onClick={() => refetch()} className="underline hover:text-text-primary">Retry</button>
          </span>
        ) : (
          <span>
            Usage since {fmtDate(usage?.since) || '…'}
            {usage?.clicksSince ? ` · clicks since ${fmtDate(usage.clicksSince)}` : ' · click tracking just started'}
          </span>
        )}
        <span className="flex-1" />
        <button onClick={() => setHidden(new Set(recommendedFor(profile.role)))}
          title="High-confidence unused items from the October 2026 usage audit"
          className="px-2.5 py-1 rounded border border-border text-text-dim hover:text-text-primary">
          Recommended
        </button>
        <button onClick={suggest} disabled={!loaded}
          className="px-2.5 py-1 rounded border border-border text-text-dim hover:text-text-primary">
          Hide screens never opened
        </button>
        <button onClick={() => setHidden(new Set())}
          className="px-2.5 py-1 rounded border border-border text-text-dim hover:text-text-primary">
          Clear
        </button>
      </div>

      {groups.map((g) => (
        <div key={g.screen}>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-text-dim mb-1.5">{g.screen}</h4>
          <div className="md:columns-2 gap-x-6">
            {g.blocks.map((block) => (
              <div key={block[0]?.key} className="break-inside-avoid mb-1">
                {block.map((item) => {
                  const u = useOf(item);
                  // Screens are judged on page views (since telemetry began); controls on clicks.
                  const measured = loaded && item.measurable !== false && (item.path ? true : clicksMeaningful);
                  const unused = measured && u.views + u.clicks === 0;
                  return (
                    <label key={item.key}
                      className={cn('flex items-center gap-2 py-1 text-sm cursor-pointer', item.sub && 'pl-5')}>
                      <input type="checkbox" checked={hidden.has(item.key)} onChange={() => toggle(item.key)} />
                      <span className={cn('flex-1 truncate', hidden.has(item.key) ? 'text-text-dim line-through' : 'text-text-primary')}>
                        {item.label}
                      </span>
                      <span className={cn('text-xs tabular-nums', unused ? 'text-amber-400' : 'text-text-dim')}
                        title={[item.note, u.last ? `Last used ${fmtDate(u.last)}` : null,
                          loaded && !item.path && item.measurable !== false && !clicksMeaningful ? `Click history starts ${fmtDate(usage?.clicksSince)}; judge after ${CLICK_HISTORY_DAYS} days` : null]
                          .filter(Boolean).join(' · ') || 'No recorded use'}>
                        {!loaded ? (isLoading ? '…' : '—') : item.measurable === false ? 'not measured' : item.path ? `${u.views} views` : `${u.clicks} clicks`}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end gap-2">
        <button onClick={onClose}
          className="px-3 py-1.5 rounded text-xs border border-border text-text-dim hover:text-text-primary">Cancel</button>
        <button onClick={handleSave} disabled={save.isPending}
          className="px-3 py-1.5 bg-accent text-bg rounded text-xs font-medium hover:bg-accent-hover">
          {save.isPending ? 'Saving…' : `Save (${hidden.size} hidden)`}
        </button>
      </div>
    </div>
  );
}

export default function FocusSetsPanel() {
  const { data: profiles = [] } = useProfiles();
  const { members = {} } = useFocus();
  const [editing, setEditing] = useState(null);
  const people = profiles.filter((p) => !p.isExternal);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">Focus sets</h3>
        <p className="text-xs text-text-dim mt-0.5">
          What each person's Focus switch hides. They can always flip to Full; hidden screens stay reachable by link and Ctrl+K.
        </p>
      </div>
      {people.map((p) => {
        const entry = members[p.id] || { mode: 'focus', hidden: [] };
        const open = editing === p.id;
        return (
          <div key={p.id} className="p-3 bg-surface border border-border rounded">
            <div className="flex items-center gap-3">
              <span className="text-sm text-text-primary flex-1">{p.fullName || p.email}</span>
              <span className="text-xs text-text-dim">
                {entry.hidden.length ? `${entry.hidden.length} hidden · on ${entry.mode === 'full' ? 'Full' : 'Focus'}` : 'Nothing hidden'}
              </span>
              <button onClick={() => setEditing(open ? null : p.id)}
                className="px-2.5 py-1 rounded text-xs border border-border text-text-dim hover:text-text-primary">
                {open ? 'Close' : 'Edit'}
              </button>
            </div>
            {open && <MemberEditor profile={p} current={entry.hidden} onClose={() => setEditing(null)} />}
          </div>
        );
      })}
    </div>
  );
}
